import React, { useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { Button, ETIDialog, ETIDialogHeader, ETIDialogBody, ETIDialogFooter, IconButton, FormContentOption, Select, Typo } from '@zawarski/eti-typescript-components';
import { Grid, CircularProgress, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import useStyles from './ModalOptions.styles';
import { ILoginForm } from 'hooks/useLoginForm';
import { YesNo } from '@/common/enum';
import { RadioSelectOptions } from 'interfaces/SelectOptions.interface.ts';
import { Stations } from '@/common/static';
import { convertToTitleCase } from '@/utils';

type ModalOptionsViewProps = {
  processing: boolean;
  form: UseFormReturn<ILoginForm>;
  onConfirm: () => void;
  goToAdmin: () => void;
  cbCancel?: () => void;
};

// Posts are managed from the admin forms — no "Create post" entry here.
const buildPostOptions = (posts?: { index?: number; posts?: string | null }[]): RadioSelectOptions[] => {
  const tempPostOptions: RadioSelectOptions[] = [];
  if (posts && posts.length && posts.some((post) => post.posts && post.posts.length)) {
    posts.forEach((post) => {
      tempPostOptions.push({
        label: post.posts ?? '',
        value: String(post.index) ?? 'empty',
      });
    });
  }
  return tempPostOptions;
};

const ModalOptionsView: React.FC<ModalOptionsViewProps> = ({ onConfirm, goToAdmin, form, processing, ...props }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  // useWatch (not form.watch) so this component re-renders when these values are
  // updated via setValue from outside — e.g. the background station-setup refresh
  // replacing 'receptions' after the modal is already open. form.watch only
  // re-renders the component that owns the form, which doesn't reach this view.
  const { control } = form;
  const isOpen = useWatch({ control, name: 'isOpen' });
  const isAdmin = useWatch({ control, name: 'isAdmin' });
  const receptionCenterOptions = useWatch({ control, name: 'receptionCentersOptions' });
  const receptionCenterID = useWatch({ control, name: 'receptionCenterID' });
  const receptions = useWatch({ control, name: 'receptions' });
  const selectedPost = useWatch({ control, name: 'selectedPost' });
  const stationValue = useWatch({ control, name: 'station' });
  const errorMsg = useWatch({ control, name: 'errorMsg' });
  const hasError = errorMsg && errorMsg.length > 0;
  const noPost = stationValue === '4' || stationValue === '5';

  const activeReception = useMemo(() => {
    return receptions.find((reception) => reception.pvDataID === receptionCenterID);
  }, [receptionCenterID, receptions]);
  const hasStationOne = activeReception?.settingsS1.isActive === YesNo.Yes;
  const disableConfirm = useMemo(() => {
    if (activeReception) {
      if (stationValue === '1') {
        return selectedPost === null || selectedPost === 'empty';
      }
      if (stationValue === '2') {
        return selectedPost === null || selectedPost === 'empty';
      }
      if (stationValue === '3') {
        return selectedPost === null || selectedPost === 'empty';
      }
      if (stationValue === '4' || stationValue === '5') {
        return false;
      }
    }
    return true;
  }, [stationValue, selectedPost, activeReception]);

  const stationOptionsDefault: RadioSelectOptions[] = useMemo(() => Stations.map((item) => ({ label: convertToTitleCase(item.title), value: item.value })), []);

  // Station and Post options are DERIVED during render rather than written into the
  // form by effects. Effects keyed on these values do not re-run when the data is
  // replaced with deep-equal content (useWatch bails out), which left a stale
  // stored postsOptions behind — e.g. reopening the modal for the station you are
  // already on showed only "Create new post" despite posts existing.
  const showStationMenu = Boolean(receptionCenterID && activeReception);
  const stationOptions = useMemo(() => {
    if (!hasStationOne) {
      return stationOptionsDefault.filter((station) => station.value !== '1');
    }
    return stationOptionsDefault;
  }, [hasStationOne, stationOptionsDefault]);

  const postOptions: RadioSelectOptions[] = useMemo(() => {
    if (receptionCenterID && activeReception && stationValue) {
      if (stationValue === '1' && hasStationOne) {
        return buildPostOptions(activeReception.settingsS1.availablePosts);
      }
      if (stationValue === '2') {
        return buildPostOptions(activeReception.settingsS2.availablePosts);
      }
      if (stationValue === '3') {
        return buildPostOptions(activeReception.settingsS3.availablePosts);
      }
    }
    return [];
  }, [hasStationOne, stationValue, receptionCenterID, activeReception]);

  const showPostMenu = Boolean(receptionCenterID && activeReception && ((stationValue === '1' && hasStationOne) || stationValue === '2' || stationValue === '3'));

  const onClose = () => {
    if (props.cbCancel) {
      props.cbCancel();
    }
    form.setValue('isOpen', false);
  };

  return (
    <ETIDialog open={isOpen} maxWidth={'md'}>
      <ETIDialogHeader title={'SELECT OPTIONS'} titleVariant={'h6'} titleColor={'primary'} subtitle={'Choose a reception center and designated station or setup a new station.'} subtitleVariant={'body2'} subtitleColor={'#707070'} closeButton={<IconButton color={'secondary'} size={'small'} onClick={onClose} children={<CloseIcon sx={{ color: '#707070' }} />} />} />
      <ETIDialogBody
        dividers={true}
        sx={{ padding: '8px' }}
        children={[
          <Box key={'1'} sx={classes.content}>
            {!!isAdmin && (
              <Button color={'primary'} fullWidth onClick={goToAdmin}>
                LOGIN AS ADMIN
              </Button>
            )}
            <FormContentOption title='STATION SELECTION'>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller control={form.control} name='receptionCenterID' render={({ field: { value, onChange }, fieldState: { error } }) => <Select required size={'small'} color={'primary'} selectOptions={receptionCenterOptions} label='Reception center' error={Boolean(error)} helperText={error ? error.message : ''} value={value} onChange={onChange} />} />
                </Grid>
                {showStationMenu && (
                  <Grid item xs={12}>
                    <Controller control={form.control} name='station' render={({ field: { value, onChange }, fieldState: { error } }) => <Select required size={'small'} color={'primary'} selectOptions={stationOptions} label='Station' error={Boolean(error)} helperText={error ? error.message : ''} value={String(value)} onChange={onChange} />} />
                  </Grid>
                )}
                {!noPost && showPostMenu && (
                  <Grid item xs={12}>
                    <Controller control={form.control} name='selectedPost' render={({ field: { value, onChange }, fieldState: { error } }) => <Select required size={'small'} color={'primary'} selectOptions={postOptions} label='Post' error={Boolean(error)} helperText={error ? error.message : ''} value={String(value)} onChange={onChange} />} />
                  </Grid>
                )}
              </Grid>
            </FormContentOption>
            {hasError && (
              <Box sx={classes.errorWrapper}>
                <Typo sx={classes.error}>{errorMsg}</Typo>
              </Box>
            )}
          </Box>,
        ]}
      />
      <ETIDialogFooter>
        <Button color={'secondary'} disabled={processing} onClick={onClose}>
          CANCEL
        </Button>
        <Button color={'primary'} endIcon={processing ? <CircularProgress size={20} /> : null} disabled={disableConfirm} onClick={onConfirm}>
          CONFIRM
        </Button>
      </ETIDialogFooter>
    </ETIDialog>
  );
};

export default ModalOptionsView;
